import * as React from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  registerSourceForStoreMutation,
  registerSourceIntentMutation,
} from '../../graphql/mutations';
import { AppConfig } from '@/utils/config';
import { paymentMethodTypes } from '@/utils/paymentMethods';
import { AddPaymentModalProps } from './types';
import i18n from '@/utils/i18n';

const stripePromise = loadStripe(AppConfig.STRIPE_PUBLIC_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

export default function AddCreditCardModalWrapper(props: AddPaymentModalProps) {
  return (
    <Elements stripe={stripePromise}>
      <AddCreditCardModal {...props} />
    </Elements>
  );
}

function AddCreditCardModal(props: AddPaymentModalProps) {
  const { isOpen, toggle, storeId } = props;
  const [creditCardMetadata, setCreditCardMetadata] = useState<any>({});
  const [clientSecret, setClientSecret] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();

  const [registerSourceIntent, { loading: loadingSourceIntent }] = useMutation(
    registerSourceIntentMutation,
    {
      onCompleted: async (mutationResultData: any) => {
        console.log({ mutationResultData });
        if (mutationResultData.registerSourceIntent) {
          setClientSecret(
            mutationResultData.registerSourceIntent.client_secret,
          );
        }
      },
    },
  );

  const [registerSourceForStore, { loading: updatingSource }] = useMutation(
    registerSourceForStoreMutation,
  );

  useEffect(() => {
    if (!isOpen) return;

    console.log('registerSourceIntent');
    registerSourceIntent({
      variables: {
        user_id: storeId,
        type: paymentMethodTypes.CARD,
      },
    });
  }, [isOpen]);

  const handleSubmit = async (event: any) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const card = elements.getElement(CardElement);

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        // @ts-ignore
        card,
        billing_details: {
          ...creditCardMetadata,
        },
      },
    });

    if (result.error) {
      // Show error to your customer.
      alert('Fehler: ' + result.error.message);
    } else {
      await registerSourceForStore({
        variables: {
          store_id: storeId,
          // @ts-ignore
          token: result.setupIntent.payment_method,
          type: paymentMethodTypes.CARD,
        },
      });
      // close modal
      toggle();
    }
    setIsLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => toggle()}
      className={'show-sepa-modal'}
      size="lg"
    >
      <ModalHeader>{i18n.t('Payment:addcard')}</ModalHeader>
      <ModalBody>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:name')}
          </span>
          <input
            type="text"
            className="form-control"
            name="iban-name"
            autoComplete="off"
            onChange={(e) =>
              setCreditCardMetadata({
                ...creditCardMetadata,
                name: e.target.value,
              })
            }
            value={creditCardMetadata.name || ''}
          />
        </div>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary text-white"
          disabled={
            isLoading || loadingSourceIntent || !clientSecret || !stripe
          }
          onClick={handleSubmit}
        >
          {isLoading ? i18n.t('Common:loading') : i18n.t('Payment:addcard')}
        </Button>{' '}
        <Button color="secondary" onClick={() => toggle()}>
          {i18n.t('Common:cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
